import { useContext } from 'react';

import { Delete } from '@mui/icons-material';
import { Button, Card, CardActions, CardContent, CardMedia, Chip, Typography } from '@mui/material';

import { ConfirmDialog } from '../../../components/dialogs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { useDeleteFeaturedBanner } from '../../../lib/featured-banner/hooks/useDeleteFeaturedBanner';
import { FeaturedBannerType } from '../../../types/FeaturedBanner';

type FeaturedBannerCardProps = {
  id: string;
  image: string | null;
  title: string;
  type: FeaturedBannerType;
};

const FeaturedBannerCard = ({ id, image, title, type }: FeaturedBannerCardProps) => {
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const { mutateAsync: deleteFeaturedBanner } = useDeleteFeaturedBanner();

  const handleDelete = async () => {
    try {
      await deleteFeaturedBanner(id);
    } catch (error) {
      setAlertSnackbar({
        message: (error as any).message || 'Failed to delete featured banner',
        severity: 'error',
        vertical: 'top',
      });
    }
  };

  return (
    <Card sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardMedia component="img" height="300" image={image || '/images/empty-image.png'} alt={title} sx={{ objectFit: 'cover' }} />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h5" component="div" noWrap>
          {title}
        </Typography>
        <Chip variant="outlined" label={type} sx={{ textTransform: 'capitalize' }} />
      </CardContent>
      <CardActions>
        <ConfirmDialog
          trigger={(open) => (
            <Button onClick={open} color="error" type="button" startIcon={<Delete />}>
              Remove
            </Button>
          )}
          title="Remove Featured Banner"
          description="Are you sure you want to remove this from featured banner?"
          onConfirm={async (close) => {
            await handleDelete();
            close();
          }}
          cancelBtnText="Cancel"
        ></ConfirmDialog>
      </CardActions>
    </Card>
  );
};

export default FeaturedBannerCard;
